import DefaultDialog from "@client/components/CustomerPortal/ui-components/dialog/dialog";
import TextInput from "@client/components/CustomerPortal/ui-components/input/TextInput";
import { Stack } from "@mui/material";
import { t } from "i18next";
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { GroupableObject, NavigationLevel } from "@nendaTypes/NendaTypes";
import {
  createCompanyGroup,
  CreateGroupPayload,
  createPremiseGroup,
} from "@client/components/CustomerPortal/store/reducers/groupReducer";
import { GroupProps } from "@nendaTypes/RequestTypes";

interface CreateGroupFormProps {
  onClose: () => void;
  isOpen: boolean;
  createFor: NavigationLevel.COMPANY | NavigationLevel.PREMISE;
  parentId: string;
}

const CreateGroupForm: React.FC<CreateGroupFormProps> = ({
  isOpen,
  onClose,
  createFor,
  parentId,
}) => {
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: { name: "" },
    validationSchema: yup.object({
      name: yup
        .string()
        .required(
          t("customerportal.pages.groups.form.validation.name_required") ||
            "Required"
        ),
    }),
    onSubmit: (values) => {
      const payload: CreateGroupPayload = {
        companyOrPremiseId: parentId,
        group: { name: values.name, applicableTo: [GroupableObject.Screen] },
      };

      if (createFor === NavigationLevel.COMPANY) {
        dispatch(createCompanyGroup(payload));
      } else {
        dispatch(createPremiseGroup(payload));
      }
    },
  });

  const handleClose = async ({ isConfirmed }: { isConfirmed: boolean }) => {
    if (isConfirmed) {
      await formik.setTouched({ name: true }, true);
      const errors = await formik.validateForm();

      if (Object.keys(errors).length === 0) {
        await formik.submitForm();
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <DefaultDialog
      open={isOpen}
      onClose={handleClose}
      title={t("customerportal.pages.groups.create_group") || "Create Group"}
    >
      <Stack sx={{ mt: 4, mb: 2 }} spacing={2}>
        <TextInput
          name="name"
          label={t("customerportal.pages.groups.form.name") || "Group Name"}
          placeholder={
            t("customerportal.pages.groups.form.name") || "Group Name"
          }
          fullWidth
          required
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={(formik.touched.name && formik.errors.name) || " "}
        />
      </Stack>
    </DefaultDialog>
  );
};
export default CreateGroupForm;
